import { useCallback, useEffect } from 'react'
import type { MapRef } from 'react-map-gl/mapbox'
import {
  DEFAULT_MAP_LIGHT_PRESET,
  MAPBOX_TERRAIN,
  MAPBOX_TERRAIN_SOURCE,
  MAPBOX_TERRAIN_SOURCE_ID,
  getMapboxConfig,
  type MapLightPreset,
} from './map3DConfig'

type MapInstance = ReturnType<MapRef['getMap']>

function applyStandardBasemapConfig(map: MapInstance, lightPreset: MapLightPreset) {
  Object.entries(getMapboxConfig(lightPreset).basemap).forEach(([key, value]) => {
    map.setConfigProperty('basemap', key, value)
  })
}

function applyTerrain(map: MapInstance) {
  if (!map.getSource(MAPBOX_TERRAIN_SOURCE_ID)) {
    map.addSource(MAPBOX_TERRAIN_SOURCE_ID, MAPBOX_TERRAIN_SOURCE)
  }

  const terrain = map.getTerrain()
  if (terrain?.source !== MAPBOX_TERRAIN.source || terrain?.exaggeration !== MAPBOX_TERRAIN.exaggeration) {
    map.setTerrain(MAPBOX_TERRAIN)
  }
}

export function useMap3DStyle({
  mapRef,
  isMapLoaded,
  lightPreset = DEFAULT_MAP_LIGHT_PRESET,
}: {
  mapRef: React.RefObject<MapRef | null>
  isMapLoaded: boolean
  lightPreset?: MapLightPreset
}) {
  const apply3DStyle = useCallback(() => {
    const map = mapRef.current?.getMap()
    if (!map?.isStyleLoaded()) return

    applyStandardBasemapConfig(map, lightPreset)
    applyTerrain(map)
  }, [lightPreset, mapRef])

  useEffect(() => {
    if (!isMapLoaded) return
    const map = mapRef.current?.getMap()
    if (!map) return

    apply3DStyle()
    map.on('style.load', apply3DStyle)

    return () => {
      map.off('style.load', apply3DStyle)
    }
  }, [apply3DStyle, isMapLoaded, mapRef])
}
